import React, { useEffect, useState } from 'react';
import { MoreVertical, ChevronDown, ChevronRight, MessageSquare, Briefcase, Code, Settings, Search, Users, Edit2, Check, X, Download, KeyRound, UserPlus, Globe2 } from 'lucide-react';
import APP_CONFIG from '../config';
import './Sidebar.css';

const Sidebar = ({ 
  agents, 
  groups,
  activeChat, 
  onSelectChat, 
  onAgentAction,
  currentTab,
  onTabChange,
  expandedAgents: externalExpandedAgents,
  onExpandAgent,
  activeView: externalActiveView,
  onViewChange,
  onAddGroup,
  activityLogs = [],
  onClearLogs,
  currentUserRole = 'user',
  onChangePassword,
  onCreateUser,
  onListUsers,
  onDeleteUser,
  onExportData,
  onExportExcel,
  isMobileOpen = false,
  onMobileClose,
  hydrating = false,
}) => {
  const [localExpandedAgents, setLocalExpandedAgents] = useState(new Set());
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [localActiveView, setLocalActiveView] = useState('chats');
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [editingAgentName, setEditingAgentName] = useState('');
  const [detailsEditor, setDetailsEditor] = useState(null);
  const [hoveredSubChat, setHoveredSubChat] = useState(null);
  const [subChatMenuOpen, setSubChatMenuOpen] = useState(null);
  const [editingSubChatId, setEditingSubChatId] = useState(null);
  const [editingSubChatName, setEditingSubChatName] = useState('');
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [groupMenuOpen, setGroupMenuOpen] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const browserTimezone = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
  })();
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '' });
  const [passwordStatus, setPasswordStatus] = useState('');
  const [newUserForm, setNewUserForm] = useState({ username: '', password: '', role: 'user' });
  const [newUserStatus, setNewUserStatus] = useState('');
  const [dataStatus, setDataStatus] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersStatus, setUsersStatus] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [apiExtensions, setApiExtensions] = useState({
    webSearch: true,
    dataAnalysis: false,
    imageGeneration: false,
    codeExecution: true,
  });

  // Use external expandedAgents if provided, otherwise use local state
  const expandedAgents = externalExpandedAgents || localExpandedAgents;
  
  // Use external activeView if provided, otherwise use local state
  const activeView = externalActiveView !== undefined ? externalActiveView : localActiveView;

  useEffect(() => {
    if (!menuOpen && !subChatMenuOpen && !groupMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!event.target.closest('.agent-menu-wrapper')) {
        setMenuOpen(null);
      }
      if (!event.target.closest('.sub-chat-menu-wrapper')) {
        setSubChatMenuOpen(null);
      }
      if (!event.target.closest('.group-menu-wrapper')) {
        setGroupMenuOpen(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(null);
        setSubChatMenuOpen(null);
        setGroupMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, subChatMenuOpen, groupMenuOpen]);

  const handleViewChange = (view) => {
    if (onViewChange) {
      onViewChange(view);
    } else {
      setLocalActiveView(view);
    }
  };

  const toggleExpand = (agentId) => {
    if (onExpandAgent) {
      // If parent component controls the state
      onExpandAgent(agentId);
    } else {
      // Otherwise use local state
      const newExpanded = new Set(localExpandedAgents);
      if (newExpanded.has(agentId)) {
        newExpanded.delete(agentId);
      } else {
        newExpanded.add(agentId);
      }
      setLocalExpandedAgents(newExpanded);
    }
  };

  const handleMenuAction = (action, agentId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (action === 'delete') {
      const agent = agents.find(a => a.id === agentId);
      if (agent && window.confirm(`Delete agent "${agent.name}"? This cannot be undone.`)) {
        onAgentAction('delete', agentId);
      }
      setMenuOpen(null);
    } else if (action === 'rename') {
      const agent = agents.find(a => a.id === agentId);
      setEditingAgentId(agentId);
      setEditingAgentName(agent.name);
      setMenuOpen(null);
    } else if (action === 'editDetails') {
      const agent = agents.find(a => a.id === agentId);
      if (!agent) return;
      setDetailsEditor({
        id: agent.id,
        name: agent.name,
        role: agent.role || '',
        personality: agent.description || '',
      });
      setMenuOpen(null);
    } else {
      onAgentAction(action, agentId);
      setMenuOpen(null);
    }
  };

  const handleSaveDetails = (event) => {
    event.preventDefault();
    if (!detailsEditor) return;
    onAgentAction('editDetails', detailsEditor.id, {
      role: detailsEditor.role.trim(),
      personality: detailsEditor.personality.trim(),
    });
    setDetailsEditor(null);
  };

  const handleSubChatMenuAction = (action, agentId, subChatId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (action === 'delete') {
      const agent = agents.find(a => a.id === agentId);
      const subChat = agent?.subChats?.find(sc => sc.id === subChatId);
      if (agent && subChat && window.confirm(`Delete sub-chat "${subChat.name}"? This cannot be undone.`)) {
        onAgentAction('deleteSubChat', agent.id, subChat.id);
      }
      setSubChatMenuOpen(null);
    } else if (action === 'rename') {
      const agent = agents.find(a => a.id === agentId);
      const subChat = agent?.subChats?.find(sc => sc.id === subChatId);
      setEditingSubChatId(subChatId);
      setEditingSubChatName(subChat?.name || '');
      setSubChatMenuOpen(null);
    }
  };

  const handleSaveEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (editingAgentName.trim() && editingAgentId) {
      onAgentAction('rename', editingAgentId, editingAgentName.trim());
      setEditingAgentId(null);
      setEditingAgentName('');
    }
  };

  const handleSaveSubChatEdit = (agentId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (editingSubChatName.trim() && editingSubChatId) {
      onAgentAction('renameSubChat', agentId, { subChatId: editingSubChatId, newName: editingSubChatName.trim() });
      setEditingSubChatId(null);
      setEditingSubChatName('');
    }
  };

  const handleCancelEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingAgentId(null);
    setEditingAgentName('');
  };

  const handleCancelSubChatEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingSubChatId(null);
    setEditingSubChatName('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit(e);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };

  const handleSubChatEditKeyDown = (agentId, e) => {
    if (e.key === 'Enter') {
      handleSaveSubChatEdit(agentId, e);
    } else if (e.key === 'Escape') {
      handleCancelSubChatEdit(e);
    }
  };

  const handleGroupMenuAction = (action, groupId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (action === 'delete') {
      const group = groups.find(g => g.id === groupId);
      if (group && window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) {
        onAgentAction('deleteGroup', group.id);
      }
      setGroupMenuOpen(null);
    } else if (action === 'rename') {
      const group = groups.find(g => g.id === groupId);
      setEditingGroupId(groupId);
      setEditingGroupName(group.name);
      setGroupMenuOpen(null);
    } else if (action === 'viewMembers') {
      const group = groups.find(g => g.id === groupId);
      if (group) {
        window.alert(group.members?.map((member) => member.name).join('\n') || 'No members');
      }
      setGroupMenuOpen(null);
    }
  };

  const handleSaveGroupEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (editingGroupName.trim() && editingGroupId) {
      onAgentAction('renameGroup', editingGroupId, editingGroupName.trim());
      setEditingGroupId(null);
      setEditingGroupName('');
    }
  };

  const handleCancelGroupEdit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const handleGroupEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveGroupEdit(e);
    } else if (e.key === 'Escape') {
      handleCancelGroupEdit(e);
    }
  };

  const tabs = [
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    { id: 'project', label: 'Logs', icon: Briefcase },
    { id: 'api', label: 'API', icon: Code },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Filter agents based on search term
  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter groups based on search term
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    setPasswordStatus('');
    const result = await onChangePassword?.(passwordForm);
    if (result?.ok) {
      setPasswordForm({ oldPassword: '', newPassword: '' });
      setPasswordStatus('Password updated.');
    } else {
      setPasswordStatus(result?.error || 'Unable to update password.');
    }
  };

  const submitCreateUser = async (e) => {
    e.preventDefault();
    setNewUserStatus('');
    const username = newUserForm.username.trim();
    if (username.length < 3) {
      setNewUserStatus('Username must be at least 3 characters.');
      return;
    }
    if (newUserForm.password.length < 6) {
      setNewUserStatus('Password must be at least 6 characters.');
      return;
    }

    const result = await onCreateUser?.({ ...newUserForm, username });
    if (result?.ok) {
      setNewUserForm({ username: '', password: '', role: 'user' });
      setNewUserStatus(result.message || 'User created.');
      loadAdminUsers();
    } else {
      setNewUserStatus(result?.error || 'Unable to create user.');
    }
  };

  const runDataExport = async (exporter) => {
    setDataStatus('');
    try {
      await exporter?.();
      setDataStatus('Export downloaded.');
    } catch (err) {
      setDataStatus(err.message || 'Export failed.');
    }
  };

  const loadAdminUsers = async () => {
    setUsersLoading(true);
    setUsersStatus('');
    const result = await onListUsers?.();
    setUsersLoading(false);
    if (result?.ok) {
      setAdminUsers(result.users || []);
    } else {
      setUsersStatus(result?.error || 'Unable to load users.');
    }
  };

  const removeAdminUser = async (user) => {
    setUsersStatus('');
    const result = await onDeleteUser?.(user.id);
    if (result?.ok) {
      setAdminUsers((prev) => prev.filter((item) => item.id !== user.id));
      setUsersStatus(`Removed ${user.username}.`);
    } else {
      setUsersStatus(result?.error || 'Unable to remove user.');
    }
  };

  const toggleApiExtension = (key) => {
    setApiExtensions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLogIcon = (type) => {
    if (type === 'success') return '✓';
    if (type === 'warning') return '⚠';
    return 'i';
  };

  return (
    <>
      {isMobileOpen && (
        <div 
          className="mobile-sidebar-overlay" 
          onClick={onMobileClose}
        />
      )}
      <div className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            {APP_CONFIG.LOGO_IMAGE ? (
              <img 
                src={`${process.env.PUBLIC_URL}/${APP_CONFIG.LOGO_IMAGE}`}
                alt={APP_CONFIG.APP_NAME}
                className="sidebar-logo-image"
              />
            ) : (
              APP_CONFIG.LOGO_LETTER
            )}
          </div>
          <span className="sidebar-logo-text">{APP_CONFIG.APP_NAME}</span>
        </div>
      </div>

      <div className="sidebar-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`sidebar-tab ${currentTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
              title={tab.label}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {currentTab === 'chat' && (
        <>
          {/* Search Bar */}
          <div className="sidebar-search-container">
            <Search size={16} className="sidebar-search-icon" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sidebar-search-input"
            />
          </div>

          <div className="sidebar-section">
            {activeView === 'chats' ? (
              <>
                <div className="sidebar-section-header">
                  <span className="text-mono">AGENTS</span>
                </div>

                <div className="agents-list">
                  {filteredAgents.length === 0 ? (
                    <div className="empty-search-state">
                      <p className="text-muted">
                        {hydrating ? 'Loading agents…' : 'No agents found'}
                      </p>
                    </div>
                  ) : (
                    filteredAgents.map((agent, index) => (
                      <div 
                        key={agent.id} 
                        className="agent-item-wrapper"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div
                          className={`agent-item ${activeChat?.id === agent.id && activeChat?.type === 'agent' ? 'active' : ''}`}
                          onMouseEnter={() => setHoveredAgent(agent.id)}
                          onMouseLeave={() => setHoveredAgent(null)}
                        >
                          <div 
                            className="agent-item-main"
                            onClick={() => onSelectChat({ type: 'agent', id: agent.id })}
                          >
                            <div className="agent-avatar">
                              <span>{agent.avatar}</span>
                              <div className={`agent-status ${agent.status}`}></div>
                            </div>
                            <div className="agent-info">
                              {editingAgentId === agent.id ? (
                                <div className="agent-name-edit-container" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={editingAgentName}
                                    onChange={(e) => setEditingAgentName(e.target.value)}
                                    onKeyDown={handleEditKeyDown}
                                    className="agent-name-edit-input"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="agent-name-edit-actions">
                                    <button
                                      className="agent-name-edit-button save"
                                      onClick={handleSaveEdit}
                                      title="Save"
                                    >
                                      <Check size={14} />
                                    </button>
                                    <button
                                      className="agent-name-edit-button cancel"
                                      onClick={handleCancelEdit}
                                      title="Cancel"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="agent-name-container">
                                  <div className="agent-name">{agent.name}</div>
                                  {agent.subChats && agent.subChats.length > 0 && (
                                    <button
                                      className="agent-expand-toggle"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(agent.id);
                                      }}
                                    >
                                      {expandedAgents.has(agent.id) ? 
                                        <ChevronDown size={14} /> : 
                                        <ChevronRight size={14} />
                                      }
                                    </button>
                                  )}
                                </div>
                              )}
                              <div className="agent-description text-muted">{agent.description}</div>
                            </div>
                          </div>

                          <div className="agent-actions">
                            {hoveredAgent === agent.id && editingAgentId !== agent.id && (
                              <div className="agent-menu-wrapper">
                                <button
                                  className="agent-action-button"
                                  onClick={() => setMenuOpen(menuOpen === agent.id ? null : agent.id)}
                                >
                                  <MoreVertical size={14} />
                                </button>

                                {menuOpen === agent.id && (
                                  <div className="agent-menu animate-slide-in-up">
                                    <button 
                                      className="agent-menu-item"
                                      onClick={(e) => handleMenuAction('rename', agent.id, e)}
                                    >
                                      <Edit2 size={14} />
                                      <span>Rename Agent</span>
                                    </button>
                                    <button
                                      className="agent-menu-item"
                                      onClick={(e) => handleMenuAction('editDetails', agent.id, e)}
                                    >
                                      <Edit2 size={14} />
                                      <span>Edit Role / Personality</span>
                                    </button>
                                    <button 
                                      className="agent-menu-item"
                                      onClick={(e) => handleMenuAction('duplicate', agent.id, e)}
                                    >
                                      <span>Duplicate Agent</span>
                                    </button>
                                    <button 
                                      className="agent-menu-item"
                                      onClick={(e) => handleMenuAction('createSubChat', agent.id, e)}
                                    >
                                      <span>Create Sub-Chat</span>
                                    </button>
                                    <button 
                                      className="agent-menu-item danger"
                                      onClick={(e) => handleMenuAction('delete', agent.id, e)}
                                    >
                                      <span>Delete Agent</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Sub-chats */}
                        {agent.subChats && agent.subChats.length > 0 && expandedAgents.has(agent.id) && (
                          <div className="sub-chats">
                            {agent.subChats.map(subChat => (
                              <div
                                key={subChat.id}
                                className={`sub-chat-item ${activeChat?.id === subChat.id && activeChat?.type === 'subChat' ? 'active' : ''}`}
                                onMouseEnter={() => setHoveredSubChat(subChat.id)}
                                onMouseLeave={() => setHoveredSubChat(null)}
                              >
                                <div 
                                  className="sub-chat-main"
                                  onClick={() => onSelectChat({ type: 'subChat', id: subChat.id, parentId: agent.id })}
                                >
                                  <div className="sub-chat-indicator"></div>
                                  {editingSubChatId === subChat.id ? (
                                    <div className="sub-chat-name-edit-container" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="text"
                                        value={editingSubChatName}
                                        onChange={(e) => setEditingSubChatName(e.target.value)}
                                        onKeyDown={(e) => handleSubChatEditKeyDown(agent.id, e)}
                                        className="sub-chat-name-edit-input"
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div className="sub-chat-name-edit-actions">
                                        <button
                                          className="sub-chat-name-edit-button save"
                                          onClick={(e) => handleSaveSubChatEdit(agent.id, e)}
                                          title="Save"
                                        >
                                          <Check size={12} />
                                        </button>
                                        <button
                                          className="sub-chat-name-edit-button cancel"
                                          onClick={handleCancelSubChatEdit}
                                          title="Cancel"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="sub-chat-name-container">
                                      <span className="sub-chat-name">{subChat.name}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {hoveredSubChat === subChat.id && editingSubChatId !== subChat.id && (
                                  <div className="sub-chat-menu-wrapper">
                                    <button
                                      className="sub-chat-action-button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSubChatMenuOpen(subChatMenuOpen === subChat.id ? null : subChat.id);
                                      }}
                                    >
                                      <MoreVertical size={12} />
                                    </button>

                                    {subChatMenuOpen === subChat.id && (
                                      <div className="sub-chat-menu animate-slide-in-up">
                                        <button 
                                          className="sub-chat-menu-item"
                                          onClick={(e) => handleSubChatMenuAction('rename', agent.id, subChat.id, e)}
                                        >
                                          <Edit2 size={12} />
                                          <span>Rename Sub-Chat</span>
                                        </button>
                                        <button 
                                          className="sub-chat-menu-item danger"
                                          onClick={(e) => handleSubChatMenuAction('delete', agent.id, subChat.id, e)}
                                        >
                                          <span>Delete Sub-Chat</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="sidebar-section-header">
                  <span className="text-mono">GROUPS</span>
                </div>
                <button 
                  className="add-new-group-button"
                  onClick={onAddGroup}
                  title="Create new group"
                >
                  <Users size={16} />
                  <span>New Group</span>
                </button>
                <div className="groups-list">
                  {filteredGroups.length === 0 ? (
                    <div className="empty-search-state">
                      <p className="text-muted">
                        {groups.length === 0 ? 'No groups yet' : 'No groups found'}
                      </p>
                    </div>
                  ) : (
                    filteredGroups.map(group => (
                      <div
                        key={group.id}
                        className={`group-item ${activeChat?.id === group.id && activeChat?.type === 'group' ? 'active' : ''}`}
                        onMouseEnter={() => setHoveredGroup(group.id)}
                        onMouseLeave={() => setHoveredGroup(null)}
                      >
                        <div 
                          className="group-item-main"
                          onClick={() => onSelectChat({ type: 'group', id: group.id })}
                        >
                          <div className="group-avatar">{group.avatar || '👥'}</div>
                          <div className="group-info">
                            {editingGroupId === group.id ? (
                              <div className="group-name-edit-container" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editingGroupName}
                                  onChange={(e) => setEditingGroupName(e.target.value)}
                                  onKeyDown={handleGroupEditKeyDown}
                                  className="group-name-edit-input"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="group-name-edit-actions">
                                  <button
                                    className="group-name-edit-button save"
                                    onClick={handleSaveGroupEdit}
                                    title="Save"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button
                                    className="group-name-edit-button cancel"
                                    onClick={handleCancelGroupEdit}
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="group-name-container">
                                <div className="group-name">{group.name}</div>
                              </div>
                            )}
                            <div className="group-members text-muted">
                              {group.members.length} members
                            </div>
                          </div>
                        </div>

                        {hoveredGroup === group.id && editingGroupId !== group.id && (
                          <div className="group-menu-wrapper">
                            <button
                              className="group-action-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupMenuOpen(groupMenuOpen === group.id ? null : group.id);
                              }}
                            >
                              <MoreVertical size={14} />
                            </button>

                            {groupMenuOpen === group.id && (
                              <div className="group-menu animate-slide-in-up">
                                <button 
                                  className="group-menu-item"
                                  onClick={(e) => handleGroupMenuAction('rename', group.id, e)}
                                >
                                  <Edit2 size={14} />
                                  <span>Rename Group</span>
                                </button>
                                <button 
                                  className="group-menu-item"
                                  onClick={(e) => handleGroupMenuAction('viewMembers', group.id, e)}
                                >
                                  <Users size={14} />
                                  <span>View Members</span>
                                </button>
                                <button 
                                  className="group-menu-item danger"
                                  onClick={(e) => handleGroupMenuAction('delete', group.id, e)}
                                >
                                  <span>Delete Group</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* View Toggle at Bottom */}
          <div className="sidebar-view-toggle">
            <button
              className={`view-toggle-button ${activeView === 'chats' ? 'active' : ''}`}
              onClick={() => handleViewChange('chats')}
            >
              <MessageSquare size={16} />
              <span>Chats</span>
            </button>
            <button
              className={`view-toggle-button ${activeView === 'groups' ? 'active' : ''}`}
              onClick={() => handleViewChange('groups')}
            >
              <Users size={16} />
              <span>Groups</span>
            </button>
          </div>
        </>
      )}

      {currentTab === 'project' && (
        <div className="logs-panel">
          <div className="logs-header">
            <h3 className="logs-title">Activity Logs</h3>
            <button className="logs-clear-button" onClick={onClearLogs}>
              Clear All
            </button>
          </div>
          <div className="logs-list">
            {activityLogs.length === 0 ? (
              <div className="empty-search-state">
                <p className="text-muted">No logs yet</p>
              </div>
            ) : (
              activityLogs.map((log) => (
                <div key={log.id} className="log-item">
                  <div className={`log-icon log-icon-${log.type || 'info'}`}>
                    {renderLogIcon(log.type)}
                  </div>
                  <div className="log-content">
                    <div className="log-title">{log.title}</div>
                    <div className="log-description">{log.description}</div>
                    <div className="log-timestamp">{log.timestamp}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {currentTab === 'api' && (
        <div className="api-panel">
          <div className="api-header">
            <h3 className="api-title">API Extensions</h3>
          </div>

          <div className="api-section">
            <h4 className="api-section-title">Available Extensions</h4>
            <div className="api-extensions-list">
              {[
                ['webSearch', '🔌', 'Web Search', 'Enable web search capabilities'],
                ['dataAnalysis', '📊', 'Data Analysis', 'Process and analyze data files'],
                ['imageGeneration', '🎨', 'Image Generation', 'Generate images with AI'],
                ['codeExecution', '💾', 'Code Execution', 'Run and test code snippets'],
              ].map(([key, icon, name, description]) => (
                <div className="api-extension-item" key={key}>
                  <div className="api-extension-icon">{icon}</div>
                  <div className="api-extension-info">
                    <div className="api-extension-name">{name}</div>
                    <div className="api-extension-description">{description}</div>
                  </div>
                  <label className="api-toggle">
                    <input
                      type="checkbox"
                      checked={apiExtensions[key]}
                      onChange={() => toggleApiExtension(key)}
                    />
                    <span className="api-toggle-slider"></span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="api-section">
            <h4 className="api-section-title">API Keys</h4>
            <button className="api-manage-keys-button">
              <span>Manage API Keys</span>
              <span>→</span>
            </button>
          </div>
        </div>
      )}

      {currentTab === 'settings' && (
        <div className="settings-panel">
          <div className="settings-section">
            <h3 className="settings-title">Appearance</h3>
            <div className="settings-item">
              <label className="settings-label">Theme</label>
              <select className="settings-select" defaultValue="Light">
                <option>Light</option>
                <option>Dark</option>
                <option>Auto</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-title">Memory</h3>
            <div className="settings-item">
              <label className="settings-label">Scope</label>
              <select className="settings-select" defaultValue="Per Agent">
                <option>Per Agent</option>
                <option>Global</option>
                <option>Session Only</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-title">Notifications</h3>
            <div className="settings-item">
              <label className="settings-checkbox">
                <input type="checkbox" defaultChecked />
                <span>Enable notifications</span>
              </label>
            </div>
            <div className="settings-item">
              <label className="settings-checkbox">
                <input type="checkbox" />
                <span>Sound alerts</span>
              </label>
            </div>
          </div>

          {currentUserRole === 'admin' && (
            <div className="settings-section">
              <h3 className="settings-title">Data</h3>
              <div className="settings-item">
                <button className="settings-action-button" onClick={() => runDataExport(onExportData)}>
                  <Download size={15} />
                  <span>Export JSON</span>
                </button>
              </div>
              <div className="settings-item">
                <button className="settings-action-button" onClick={() => runDataExport(onExportExcel)}>
                  <Download size={15} />
                  <span>Export Excel</span>
                </button>
              </div>
              {dataStatus && <div className="settings-status">{dataStatus}</div>}
            </div>
          )}

          <div className="settings-section">
            <h3 className="settings-title">Preferences</h3>
            <div className="settings-item">
              <label className="settings-label">
                <Globe2 size={14} />
                <span>Timezone</span>
              </label>
              <div className="settings-timezone-value" title="Detected from your browser">
                {browserTimezone}
              </div>
              <p className="settings-hint">Timestamps use your device&apos;s local timezone automatically.</p>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-title">Account</h3>
            <form className="settings-form" onSubmit={submitPasswordChange}>
              <label className="settings-label">
                <KeyRound size={14} />
                <span>Change Password</span>
              </label>
              <input
                type="password"
                className="settings-input"
                placeholder="Current password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
              />
              <input
                type="password"
                className="settings-input"
                placeholder="New password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
              <button className="settings-action-button" type="submit">
                <Check size={15} />
                <span>Update Password</span>
              </button>
              {passwordStatus && <div className="settings-status">{passwordStatus}</div>}
            </form>
          </div>

          {currentUserRole === 'admin' && (
            <div className="settings-section">
              <h3 className="settings-title">Admin</h3>
              <form className="settings-form" onSubmit={submitCreateUser}>
                <label className="settings-label">
                  <UserPlus size={14} />
                  <span>Create User</span>
                </label>
                <input
                  type="text"
                  className="settings-input"
                  placeholder="Username"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                />
                <input
                  type="password"
                  className="settings-input"
                  placeholder="Password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                />
                <select
                  className="settings-select"
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                <button className="settings-action-button" type="submit">
                  <UserPlus size={15} />
                  <span>Create Account</span>
                </button>
                {newUserStatus && <div className="settings-status">{newUserStatus}</div>}
              </form>

              <div className="settings-admin-users">
                <div className="settings-admin-users-header">
                  <label className="settings-label">Users</label>
                  <button
                    className="settings-small-button"
                    type="button"
                    onClick={loadAdminUsers}
                    disabled={usersLoading}
                  >
                    {usersLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                {adminUsers.length > 0 ? (
                  <div className="settings-user-list">
                    {adminUsers.map((user) => (
                      <div className="settings-user-item" key={user.id}>
                        <div className="settings-user-info">
                          <span className="settings-user-name">{user.username}</span>
                          <span className="settings-user-role">{user.role}</span>
                        </div>
                        <button
                          className="settings-small-button danger"
                          type="button"
                          onClick={() => removeAdminUser(user)}
                          disabled={user.is_current_user}
                          title={user.is_current_user ? 'You cannot remove your own account' : 'Remove user'}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="settings-status">Click Refresh to list users.</div>
                )}
                {usersStatus && <div className="settings-status">{usersStatus}</div>}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
      {detailsEditor && (
        <>
          <div className="dialog-overlay" onClick={() => setDetailsEditor(null)} />
          <form className="agent-details-dialog" onSubmit={handleSaveDetails}>
            <div className="dialog-content agent-details-content">
              <div className="dialog-title">Edit {detailsEditor.name}</div>
              <label className="agent-details-label" htmlFor="agent-role-input">Role</label>
              <input
                id="agent-role-input"
                className="settings-input"
                value={detailsEditor.role}
                onChange={(event) => setDetailsEditor({ ...detailsEditor, role: event.target.value })}
                autoFocus
              />
              <label className="agent-details-label" htmlFor="agent-personality-input">Personality</label>
              <textarea
                id="agent-personality-input"
                className="settings-input agent-details-textarea"
                value={detailsEditor.personality}
                onChange={(event) => setDetailsEditor({ ...detailsEditor, personality: event.target.value })}
                rows={4}
              />
              <div className="dialog-actions">
                <button
                  className="dialog-button dialog-button-cancel"
                  type="button"
                  onClick={() => setDetailsEditor(null)}
                >
                  Cancel
                </button>
                <button className="dialog-button dialog-button-save" type="submit">
                  Save
                </button>
              </div>
            </div>
          </form>
        </>
      )}
    </>
  );
};

export default Sidebar;
